import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  Calendar,
  CheckCircle2,
  Clock,
  Flame,
  GraduationCap,
  Languages,
  PenLine,
  RotateCcw,
  Sparkles,
  Target,
  Timer,
  Trophy,
  XCircle,
  Zap,
} from 'lucide-react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import PageHeader from '../components/ui/PageHeader';
import SegmentedTabs from '../components/ui/SegmentedTabs';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useQuery, STALE } from '../hooks/useQuery';
import { dashboardAPI } from '../services/api';
import { supabase } from '../lib/supabaseClient';
import { Skeleton, StatCardSkeleton, ErrorState } from '../components/ui/Skeleton';
import StatCard from '../components/Dashboard/StatCard';

const MODULES = [
  {
    id: 'lesen',
    title: 'Lesen',
    emoji: '📖',
    icon: BookOpen,
    path: '/exam/b2/lesen',
    color: 'border-red-200 bg-red-50/50 dark:bg-red-950/20 text-red-600 dark:text-red-400',
    badgeColor: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200',
  },
];

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs < 10 ? '0' : ''}${secs}s`;
};

const formatDate = (isoStr, lang) => {
  if (!isoStr) return '—';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString(lang === 'de' ? 'de-DE' : 'sq-AL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoStr;
  }
};

const ProgressPage = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const [filterSkill, setFilterSkill] = useState('all');

  // 1. Fetch dashboard overview stats
  const {
    data: stats,
    isLoading: isStatsLoading,
    error: statsError,
    refetch: refetchStats,
  } = useQuery(
    'dashboard_stats',
    null,
    async () => {
      const res = await dashboardAPI.getStats();
      return res?.data;
    },
    { staleTime: STALE.short }
  );

  // 2. Fetch all Goethe B2 exam attempts
  const {
    data: examAttempts,
    isLoading: isAttemptsLoading,
    error: attemptsError,
    refetch: refetchAttempts,
  } = useQuery(
    'exam_attempts_all',
    user?.id,
    async () => {
      if (!user?.id) return [];
      const { data, error } = await supabase
        .from('exam_b2_attempts')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    { staleTime: STALE.short, enabled: !!user?.id }
  );

  const attempts = examAttempts || [];
  const xp = stats?.xp ?? user?.xp ?? 0;
  const level = user?.level ?? stats?.level ?? Math.max(1, Math.floor(xp / 500) + 1);
  const streak = stats?.streak ?? user?.streak ?? 0;
  const accuracy = stats?.successRate ?? 0;

  // Compute skill stats
  const skillSummaries = MODULES.map((mod) => {
    const skillAttempts = attempts.filter((a) => a.skill?.toLowerCase() === mod.id);
    const bestScore = skillAttempts.length > 0
      ? Math.max(...skillAttempts.map((a) => a.score || 0))
      : null;
    const latestAttempt = skillAttempts[0] || null;
    const passed = skillAttempts.some((a) => a.passed || (a.score || 0) >= 60);

    return {
      ...mod,
      totalAttempts: skillAttempts.length,
      bestScore,
      latestAttempt,
      passed,
    };
  });

  const passedCount = skillSummaries.filter((s) => s.passed).length;
  const filteredAttempts = filterSkill === 'all'
    ? attempts
    : attempts.filter((a) => a.skill?.toLowerCase() === filterSkill);

  const totalWords = stats?.vocab?.total ?? 0;
  const knownWords = stats?.vocab?.known ?? 0;
  const learningWords = stats?.vocab?.learning ?? 0;
  const dueWords = stats?.srs_due ?? 0;
  const vocabMasteryPct = totalWords > 0 ? Math.round((knownWords / totalWords) * 100) : 0;

  const currentLevelMinXp = (level - 1) * 500;
  const nextLevelXp = level * 500;
  const xpInCurrentLevel = Math.max(0, xp - currentLevelMinXp);
  const levelProgressPct = Math.min(100, Math.round((xpInCurrentLevel / 500) * 100));

  const isLoading = isStatsLoading || isAttemptsLoading;

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-7 pb-12">
        <PageHeader
          icon={BarChart3}
          badge="Goethe-Zertifikat B2 & Lernfortschritt"
          title={t('Fortschritt & Statistiken')}
          description={
            language === 'de'
              ? 'Deine Übersicht zu Prüfungsergebnissen, Goethe B2 Modulen, Wortschatz und Lernaktivitäten.'
              : 'Përmbledhje e plotë e rezultateve të provimeve Goethe B2, fjalorit dhe ecurisë tënde mësimore.'
          }
        />

        {statsError && (
          <ErrorState
            message={t('Statistikat nuk mund të ngarkoheshin.')}
            onRetry={() => {
              refetchStats();
              refetchAttempts();
            }}
          />
        )}

        {/* 1. Overview Metric Cards */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-4">
          {isLoading ? (
            [0, 1, 2, 3].map((i) => <StatCardSkeleton key={i} />)
          ) : (
            <>
              <StatCard
                title={t('Pikë XP')}
                value={xp.toLocaleString()}
                unit="XP"
                tone="gold"
                badge={{ icon: Zap, text: `${t('Niveli')} ${level}` }}
                progress={levelProgressPct}
                footer={`${xpInCurrentLevel} / 500 XP`}
                action={{ label: language === 'de' ? 'Üben' : 'Ushtro', to: '/timed-challenge' }}
              />
              <StatCard
                title={t('Ditë rresht')}
                value={streak}
                unit={language === 'de' ? 'Tage' : 'ditë'}
                tone="red"
                badge={{ icon: Flame, text: streak > 0 ? (language === 'de' ? 'aktiv' : 'aktive') : '—' }}
                notice={{
                  icon: CheckCircle2,
                  text: streak > 0
                    ? (language === 'de' ? 'Aktive Lernserie!' : 'Seri aktive!')
                    : (language === 'de' ? 'Heute starten' : 'Fillo sot'),
                  tone: streak > 0 ? 'gold' : 'red',
                }}
              />
              <StatCard
                title={t('Saktësi')}
                value={accuracy}
                unit="%"
                tone="black"
                badge={{ icon: Target, text: `${knownWords} ${language === 'de' ? 'Wörter' : 'fjalë'}` }}
                progress={accuracy}
                footer={`${knownWords} / ${totalWords} ${language === 'de' ? 'Wörter gemeistert' : 'fjalë të njohura'}`}
                action={{ label: language === 'de' ? 'Wortschatz' : 'Fjalori', to: '/vocabulary' }}
              />
              <StatCard
                title="Goethe B2"
                value={`${passedCount} / 4`}
                unit={language === 'de' ? 'Module' : 'module'}
                tone="gold"
                badge={{ icon: Trophy, text: passedCount === 4 ? (language === 'de' ? 'bestanden' : 'kaluar') : `${4 - passedCount} ${language === 'de' ? 'übrig' : 'mbetur'}` }}
                progress={(passedCount / 4) * 100}
                footer={language === 'de' ? 'Mindestens 60 % pro Modul' : 'Min. 60% për modul'}
                action={{ label: language === 'de' ? 'Prüfung' : 'Provimi', to: '/exam/b2' }}
              />
            </>
          )}
        </section>

        {/* 2. Goethe B2 4-Module Readiness Grid */}
        <section className="space-y-4">
          <div className="flex flex-col justify-between gap-1 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                {language === 'de' ? 'Goethe-Zertifikat B2 — Modulübersicht' : 'Përmbledhja e Moduleve Goethe-Zertifikat B2'}
              </h2>
              <p className="text-xs text-zinc-500">
                {language === 'de'
                  ? 'Erforderlich zum Bestehen: mindestens 60 % in jedem einzelnen Modul.'
                  : 'Kriteri për kalim: të paktën 60% në secilin modul.'}
              </p>
            </div>
            <Link
              to="/exam/b2"
              className="inline-flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700 hover:underline dark:text-red-400"
            >
              {language === 'de' ? 'Zum Goethe-B2 Hub' : 'Te qendra e Goethe B2'} →
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {skillSummaries.map((mod) => {
              const Icon = mod.icon;
              return (
                <div
                  key={mod.id}
                  className="flex flex-col justify-between rounded-xl border border-zinc-200/80 bg-white p-5 shadow-xs transition hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl" aria-hidden="true">{mod.emoji}</span>
                        <h3 className="font-extrabold text-zinc-900 dark:text-white">{t(mod.title)}</h3>
                      </div>
                      {mod.passed ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-bold text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300">
                          <CheckCircle2 size={12} /> {language === 'de' ? 'Bestanden' : 'Kaluar'}
                        </span>
                      ) : mod.totalAttempts > 0 ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                          {language === 'de' ? 'In Arbeit' : 'Në proces'}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">
                          {language === 'de' ? 'Offen' : 'E paprovuar'}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 space-y-2">
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-zinc-500">{language === 'de' ? 'Beste Note:' : 'Rezultati më i mirë:'}</span>
                        <span className="text-xl font-black text-zinc-900 dark:text-white">
                          {mod.bestScore !== null ? `${mod.bestScore}%` : '—'}
                        </span>
                      </div>

                      {mod.bestScore !== null && (
                        <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                          <div
                            className={`h-full rounded-full transition-all ${
                              mod.bestScore >= 60 ? 'bg-emerald-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${Math.min(100, mod.bestScore)}%` }}
                          />
                        </div>
                      )}

                      <div className="flex justify-between text-[11px] text-zinc-400">
                        <span>{mod.totalAttempts} {language === 'de' ? 'Versuche' : 'tentativa'}</span>
                        <span>{language === 'de' ? 'Schwelle: 60%' : 'Pragu: 60%'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 border-t border-zinc-100 pt-3 dark:border-zinc-800">
                    <Link
                      to={mod.path}
                      className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-zinc-900 px-3 py-2 text-xs font-bold text-white transition hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
                    >
                      <Icon size={14} />
                      {mod.totalAttempts > 0
                        ? (language === 'de' ? 'Erneut üben' : 'Ushtrohu sërish')
                        : (language === 'de' ? 'Jetzt starten' : 'Fillo tani')}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* 3. Detailed Exam History Table */}
        <section className="space-y-4">
          <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-center">
            <div>
              <h2 className="text-lg font-bold text-zinc-900 dark:text-white">
                {language === 'de' ? 'Prüfungshistorie & Detaillierte Ergebnisse' : 'Historiku i Provimeve & Rezultatet e Detajuara'}
              </h2>
              <p className="text-xs text-zinc-500">
                {language === 'de'
                  ? 'Chronologische Übersicht aller abgeschlossenen Versuche.'
                  : 'Lista kronologjike e të gjitha provimeve dhe vlerësimeve.'}
              </p>
            </div>

            <SegmentedTabs
              ariaLabel={language === 'de' ? 'Fertigkeit filtern' : 'Filtro sipas aftësisë'}
              value={filterSkill}
              onChange={setFilterSkill}
              tabs={[
                { id: 'all', label: language === 'de' ? 'Alle' : 'Të gjitha' },
                { id: 'lesen', label: 'Lesen' },
                { id: 'hoeren', label: 'Hören' },
                { id: 'schreiben', label: 'Schreiben' },
                { id: 'sprechen', label: 'Sprechen' },
              ]}
            />
          </div>

          <div className="overflow-hidden rounded-xl border border-zinc-200/80 bg-white shadow-xs dark:border-zinc-800 dark:bg-zinc-900">
            {isLoading ? (
              <div className="space-y-3 p-5">
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
                <Skeleton className="h-10 w-full rounded-lg" />
              </div>
            ) : filteredAttempts.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm font-medium text-zinc-500">
                  {language === 'de'
                    ? 'Noch keine Prüfungsergebnisse vorhanden. Starte jetzt einen Goethe B2 Teil!'
                    : 'Ende nuk keni rezultate provimesh. Zgjidhni një modul Goethe B2 për të filluar!'}
                </p>
                <Link
                  to="/exam/b2"
                  className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-xs font-bold text-white transition hover:bg-red-700"
                >
                  <GraduationCap size={15} />
                  {language === 'de' ? 'Zu den Prüfungen' : 'Shko te provimet'}
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-zinc-200 bg-zinc-50 text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:border-zinc-800 dark:bg-zinc-800/50 dark:text-zinc-400">
                    <tr>
                      <th className="px-4 py-3">{language === 'de' ? 'Modul' : 'Moduli'}</th>
                      <th className="px-4 py-3">{language === 'de' ? 'Ergebnis' : 'Rezultati'}</th>
                      <th className="px-4 py-3">{language === 'de' ? 'Status' : 'Statusi'}</th>
                      <th className="px-4 py-3">{language === 'de' ? 'Dauer' : 'Koha'}</th>
                      <th className="px-4 py-3">{language === 'de' ? 'Variante' : 'Varianti'}</th>
                      <th className="px-4 py-3">{language === 'de' ? 'Datum' : 'Data'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                    {filteredAttempts.map((attempt) => {
                      const skillKey = attempt.skill?.toLowerCase();
                      const mod = MODULES.find((m) => m.id === skillKey);
                      const isPassed = attempt.passed || (attempt.score || 0) >= 60;
                      return (
                        <tr key={attempt.id} className="transition hover:bg-zinc-50/80 dark:hover:bg-zinc-800/40">
                          <td className="px-4 py-3 font-semibold text-zinc-900 dark:text-white">
                            <span className="mr-1.5 inline-block" aria-hidden="true">{mod?.emoji || '📝'}</span>
                            <span className="capitalize">{mod?.title || attempt.skill}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-extrabold text-zinc-900 dark:text-white">{attempt.score ?? 0}%</span>
                              <div className="h-1.5 w-16 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                                <div
                                  className={`h-full rounded-full ${isPassed ? 'bg-emerald-500' : 'bg-red-500'}`}
                                  style={{ width: `${Math.min(100, attempt.score || 0)}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {isPassed ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
                                <CheckCircle2 size={12} /> {language === 'de' ? 'Bestanden' : 'Kaluar'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-bold text-red-700 dark:bg-red-950/50 dark:text-red-300">
                                <XCircle size={12} /> {language === 'de' ? 'Nicht bestanden' : 'Mbetur'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-zinc-500">
                            {formatDuration(attempt.time_spent_seconds)}
                          </td>
                          <td className="px-4 py-3 text-zinc-500 capitalize">
                            {attempt.variant || 'Erwachsene'}
                          </td>
                          <td className="px-4 py-3 text-zinc-500">
                            {formatDate(attempt.created_at, language)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        {/* 4. Wortschatz & Praxis Highlights */}
        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Wortschatz Box */}
          <div className="rounded-xl border border-zinc-200/80 bg-white p-5 shadow-xs dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
                  <Languages size={18} />
                </span>
                <div>
                  <h3 className="font-extrabold text-zinc-900 dark:text-white">{t('Wortschatz')}</h3>
                  <p className="text-xs text-zinc-500">{language === 'de' ? 'Lernstand & Wiederholung' : 'Gjendja e fjalorit & përsëritjet'}</p>
                </div>
              </div>
              <Link
                to="/unknown-words"
                className="text-xs font-bold text-red-600 hover:underline dark:text-red-400"
              >
                {t('Fjalët e Ditura')} →
              </Link>
            </div>

            <div className="mt-4 space-y-3">
              <div className="flex justify-between text-xs">
                <span className="text-zinc-500">{language === 'de' ? 'Gemerkte Wörter:' : 'Fjalë të njohura:'}</span>
                <span className="font-bold text-zinc-900 dark:text-white">{knownWords} / {totalWords}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
                <div className="h-full rounded-full bg-yellow-500 transition-all" style={{ width: `${vocabMasteryPct}%` }} />
              </div>

              <div className="grid grid-cols-3 gap-2 pt-2 text-center text-xs">
                <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
                  <span className="block text-xs font-bold text-zinc-900 dark:text-white">{totalWords}</span>
                  <span className="text-[10px] text-zinc-400">{language === 'de' ? 'Gesamt' : 'Gjithsej'}</span>
                </div>
                <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
                  <span className="block text-xs font-bold text-emerald-600 dark:text-emerald-400">{knownWords}</span>
                  <span className="text-[10px] text-zinc-400">{language === 'de' ? 'Gelernt' : 'Mësuar'}</span>
                </div>
                <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
                  <span className="block text-xs font-bold text-amber-600 dark:text-amber-400">{dueWords}</span>
                  <span className="text-[10px] text-zinc-400">{language === 'de' ? 'Fällig' : 'Presin'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Spiele & Praxis Links */}
          <div className="rounded-xl border border-zinc-200/80 bg-white p-5 shadow-xs dark:border-zinc-800 dark:bg-zinc-900">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                <Sparkles size={18} />
              </span>
              <div>
                <h3 className="font-extrabold text-zinc-900 dark:text-white">{t('Spiele & Praxis')}</h3>
                <p className="text-xs text-zinc-500">{language === 'de' ? 'Aktiv trainieren & festigen' : 'Ushtrime praktike & lojëra'}</p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2.5">
              <Link
                to="/timed-challenge"
                className="group flex flex-col justify-between rounded-lg border border-zinc-200/70 p-3 transition hover:border-red-300 hover:bg-red-50/20 dark:border-zinc-800 dark:hover:bg-red-950/20"
              >
                <div className="flex items-center gap-2">
                  <Timer size={16} className="text-red-500 transition group-hover:scale-110" />
                  <span className="text-xs font-bold text-zinc-900 dark:text-white">{t('Zeit-Challenge')}</span>
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  {language === 'de' ? 'Schnelligkeit & Wortschatz' : 'Shpejtësi & fjalor në kohë'}
                </p>
              </Link>

              <Link
                to="/writing-journal"
                className="group flex flex-col justify-between rounded-lg border border-zinc-200/70 p-3 transition hover:border-blue-300 hover:bg-blue-50/20 dark:border-zinc-800 dark:hover:bg-blue-950/20"
              >
                <div className="flex items-center gap-2">
                  <PenLine size={16} className="text-blue-500 transition group-hover:scale-110" />
                  <span className="text-xs font-bold text-zinc-900 dark:text-white">{t('Schreibjournal')}</span>
                </div>
                <p className="mt-1 text-[11px] text-zinc-500">
                  {language === 'de' ? 'Freies Schreiben mit KI' : 'Shkrim i lirë me korrigjim'}
                </p>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default ProgressPage;
